<template>
  <div class="create-task-flow">
    <!-- 头部：仅保留步骤指示器（大标题 / + 号已去除，与市场洞察页头风格对齐） -->
    <div class="create-task-header">
      <!-- 步骤指示器 -->
      <div class="step-indicator">
        <div class="step-item" :class="{ active: viewState === 'select-template', completed: currentStep > 1 }">
          <div class="step-dot">
            <i v-if="currentStep > 1" class="el-icon-check"></i>
            <span v-else>1</span>
          </div>
          <span class="step-label">选择模板</span>
        </div>
        <div class="step-line" :class="{ active: currentStep > 1 }"></div>
        <div class="step-item" :class="{ active: viewState === 'config-template', completed: currentStep > 2 }">
          <div class="step-dot">
            <i v-if="currentStep > 2" class="el-icon-check"></i>
            <span v-else>2</span>
          </div>
          <span class="step-label">选择定时周期</span>
        </div>
        <div class="step-line" :class="{ active: currentStep > 2 }"></div>
        <div class="step-item" :class="{ active: viewState === 'create-success' }">
          <div class="step-dot">3</div>
          <span class="step-label">任务创建</span>
        </div>
      </div>
    </div>

    <!-- Step 1: 模板选择 -->
    <div v-if="viewState === 'select-template'" class="template-select-view">
      <div class="template-list-header">
        <div class="view-intro">
          <div class="view-intro-title">选择模板</div>
          <div class="view-intro-desc" v-if="templateList.length > 0">从{{ templateList.length }}个模板中选择合适的模板</div>
          <div class="view-intro-desc" v-else>正在加载模板...</div>
        </div>
        <div class="header-actions">
          <span class="back-btn" @click="handleBackToInitial">
            <i class="el-icon-back"></i>
            <span>历史报告</span>
          </span>
        </div>
      </div>
      <div v-if="templateList.length > 0" class="template-grid">
        <div
          v-for="template in templateList"
          :key="template.id"
          class="template-card"
          >
          <div class="tc-header">
            <div class="template-icon" :style="{ background: getTemplateColor(template) }">
              <i :class="getTemplateIcon(template)"></i>
            </div>
            <div class="template-meta-info">
              <div class="template-name">{{ getMockName(template).cn }}</div>
              <div class="template-name-en">{{ getMockName(template).en }}</div>
            </div>
          </div>
          <div class="template-info">
            <div class="template-desc">{{ template.templateDesc || getMockDesc(template) }}</div>
          </div>
          <div class="tc-subsections">
            <div v-for="(sub, idx) in getMockSubsections(template).slice(0, 4)" :key="idx" class="tc-sub-item">
              <span class="tc-sub-dot"></span>
              <span class="tc-sub-text">{{ sub.title }}</span>
            </div>
          </div>
          <div class="tc-footer">
            <span class="tc-select-btn tc-select-btn-outline" @click.stop="handlePreviewTemplate(template)">
              <i class="el-icon-view"></i>
              <span>模板预览</span>
            </span>
            <span class="tc-select-btn tc-select-btn-primary" @click.stop="handleSelectTemplate(template)">
              <i class="el-icon-check"></i>
              <span>开始创建</span>
            </span>
          </div>
        </div>
      </div>
      <div v-else-if="!templateLoading && templateList.length === 0" class="template-empty">
        <i class="el-icon-folder-opened"></i>
        <p>暂无可用模板</p>
      </div>
    </div>

    <!-- Step 2: 模板配置 -->
    <div v-if="viewState === 'config-template'" class="template-config-view">
      <div class="config-layout">
        <!-- 左侧：配置表单 -->
        <div class="config-left">
          <!-- 已选模板信息 -->
          <div class="selected-template-card">
            <div class="stc-header">
              <div class="stc-icon" :style="{ background: getTemplateColor(selectedTemplate) }">
                <i :class="getTemplateIcon(selectedTemplate)"></i>
              </div>
              <div class="stc-info">
                <div class="stc-label">已选模板</div>
                <div class="stc-name">{{ selectedTemplate.templateName }}</div>
              </div>
            </div>
            <div class="stc-actions">
              <span class="stc-link" @click="viewState = 'select-template'">更换模板</span>
            </div>
          </div>

          <!-- 设置周期 + 选填信息 横向布局 -->
          <div class="config-row">
            <!-- 设置周期 -->
            <div class="config-section config-section-period">
              <div class="section-title">
                <i class="el-icon-time"></i>
                <span>设置周期</span>
                <span class="section-optional">（必选）</span>
              </div>
              <div class="period-row">
                <span class="period-label">定时周期</span>
                <el-select v-model="cronPeriod" class="period-select">
                  <el-option
                    v-for="p in periodOptions"
                    :key="p.value"
                    :label="p.label"
                    :value="p.value">
                  </el-option>
                </el-select>
              </div>
              <div class="period-row">
                <span class="period-label">执行时间</span>
                <div class="time-selects">
                  <template v-if="cronPeriod === 'daily'">
                    <span class="time-label">每天</span>
                    <el-select v-model="cronHour" class="time-sel" placeholder="时">
                      <el-option v-for="h in 24" :key="h-1" :label="padZero(h-1) + ' 时'" :value="h-1"></el-option>
                    </el-select>
                    <span class="time-sep">:</span>
                    <el-select v-model="cronMinute" class="time-sel" placeholder="分">
                      <el-option v-for="m in cronMinuteOptions" :key="m" :label="padZero(m) + ' 分'" :value="m"></el-option>
                    </el-select>
                  </template>
                  <template v-else-if="cronPeriod === 'weekly'">
                    <span class="time-label">每周</span>
                    <el-select v-model="cronWeekDay" class="time-sel" placeholder="周几">
                      <el-option
                        v-for="d in 7"
                        :key="d"
                        :label="weekDayLabel(d)"
                        :value="d"></el-option>
                    </el-select>
                    <el-select v-model="cronHour" class="time-sel" placeholder="时">
                      <el-option v-for="h in 24" :key="h-1" :label="padZero(h-1) + ' 时'" :value="h-1"></el-option>
                    </el-select>
                    <span class="time-sep">:</span>
                    <el-select v-model="cronMinute" class="time-sel" placeholder="分">
                      <el-option v-for="m in cronMinuteOptions" :key="m" :label="padZero(m) + ' 分'" :value="m"></el-option>
                    </el-select>
                  </template>
                  <template v-else-if="cronPeriod === 'monthly'">
                    <span class="time-label">每月</span>
                    <el-select v-model="cronMonthDay" class="time-sel time-sel-day" placeholder="日">
                      <el-option v-for="d in 28" :key="d" :label="d + ' 日'" :value="d"></el-option>
                    </el-select>
                    <el-select v-model="cronHour" class="time-sel" placeholder="时">
                      <el-option v-for="h in 24" :key="h-1" :label="padZero(h-1) + ' 时'" :value="h-1"></el-option>
                    </el-select>
                    <span class="time-sep">:</span>
                    <el-select v-model="cronMinute" class="time-sel" placeholder="分">
                      <el-option v-for="m in cronMinuteOptions" :key="m" :label="padZero(m) + ' 分'" :value="m"></el-option>
                    </el-select>
                  </template>
                  <template v-else-if="cronPeriod === 'quarterly'">
                    <span class="time-label">每季度第</span>
                    <el-select v-model="cronQuarterStart" class="time-sel time-sel-quarter" placeholder="起始月">
                      <el-option v-for="q in quarterOptions" :key="q.value" :label="q.label" :value="q.value"></el-option>
                    </el-select>
                    <el-select v-model="cronMonthDay" class="time-sel time-sel-day" placeholder="日">
                      <el-option v-for="d in 28" :key="d" :label="d + ' 日'" :value="d"></el-option>
                    </el-select>
                    <el-select v-model="cronHour" class="time-sel" placeholder="时">
                      <el-option v-for="h in 24" :key="h-1" :label="padZero(h-1) + ' 时'" :value="h-1"></el-option>
                    </el-select>
                    <span class="time-sep">:</span>
                    <el-select v-model="cronMinute" class="time-sel" placeholder="分">
                      <el-option v-for="m in cronMinuteOptions" :key="m" :label="padZero(m) + ' 分'" :value="m"></el-option>
                    </el-select>
                  </template>
                  <template v-else-if="cronPeriod === 'halfYear'">
                    <span class="time-label">每半年</span>
                    <el-select v-model="cronMonth" class="time-sel" placeholder="月">
                      <el-option v-for="mo in [1, 7]" :key="mo" :label="mo + ' 月'" :value="mo"></el-option>
                    </el-select>
                    <el-select v-model="cronMonthDay" class="time-sel time-sel-day" placeholder="日">
                      <el-option v-for="d in 28" :key="d" :label="d + ' 日'" :value="d"></el-option>
                    </el-select>
                    <el-select v-model="cronHour" class="time-sel" placeholder="时">
                      <el-option v-for="h in 24" :key="h-1" :label="padZero(h-1) + ' 时'" :value="h-1"></el-option>
                    </el-select>
                    <span class="time-sep">:</span>
                    <el-select v-model="cronMinute" class="time-sel" placeholder="分">
                      <el-option v-for="m in cronMinuteOptions" :key="m" :label="padZero(m) + ' 分'" :value="m"></el-option>
                    </el-select>
                  </template>
                  <template v-else-if="cronPeriod === 'yearly'">
                    <span class="time-label">每年</span>
                    <el-select v-model="cronMonth" class="time-sel" placeholder="月">
                      <el-option v-for="mo in 12" :key="mo" :label="mo + ' 月'" :value="mo"></el-option>
                    </el-select>
                    <el-select v-model="cronMonthDay" class="time-sel time-sel-day" placeholder="日">
                      <el-option v-for="d in 28" :key="d" :label="d + ' 日'" :value="d"></el-option>
                    </el-select>
                    <el-select v-model="cronHour" class="time-sel" placeholder="时">
                      <el-option v-for="h in 24" :key="h-1" :label="padZero(h-1) + ' 时'" :value="h-1"></el-option>
                    </el-select>
                    <span class="time-sep">:</span>
                    <el-select v-model="cronMinute" class="time-sel" placeholder="分">
                      <el-option v-for="m in cronMinuteOptions" :key="m" :label="padZero(m) + ' 分'" :value="m"></el-option>
                    </el-select>
                  </template>
                </div>
              </div>
              <div class="period-row">
                <span class="period-label">创建后立即执行</span>
                <el-switch
                  v-model="formData.runImmediately"
                  active-color="#4A5FF3">
                </el-switch>
              </div>
            </div>

            <!-- 选填信息：模板信息 + 补充信息 -->
            <div class="config-section config-section-optional">
              <div class="section-title">
                <i class="el-icon-document"></i>
                <span>选填信息</span>
                <span class="section-optional">（选填）</span>
              </div>
              <div v-if="templateFields.length > 0" class="template-fields-row">
                <div v-for="field in templateFields" :key="field.variableKey" class="template-field-item">
                  <div class="tf-label">
                    {{ field.variableLabel }}
                    <span v-if="!field.required" class="tf-optional">(选填)</span>
                  </div>
                  <el-input
                    v-model="formData.fields[field.variableKey]"
                    :placeholder="field.placeholder || '请输入'"
                    size="small"
                    class="tf-input">
                  </el-input>
                </div>
              </div>
              <div v-if="templateFields.length === 0" class="no-fields-tip">
                该模板暂无额外字段
              </div>
            </div>
          </div>

          <!-- 补充信息 -->
          <div class="config-section">
            <div class="section-title">
              <i class="el-icon-edit-outline"></i>
              <span>补充信息</span>
              <span class="section-optional">（选填）</span>
            </div>
            <el-input
              type="textarea"
              v-model="formData.supplement"
              :rows="3"
              placeholder="可输入补充信息，将用于报告生成的额外指令..."
              resize="none"
              class="supplement-textarea">
            </el-input>
          </div>

          <!-- 异常告警监控 -->
          <div class="config-section">
            <div class="section-title">
              <i class="el-icon-warning" style="color: #E6A23C;"></i>
              <span>异常告警监控</span>
              <span class="section-optional">（选填，可多选）</span>
            </div>
            <div class="anomaly-options">
              <label
                v-for="opt in anomalyOptions"
                :key="opt.key"
                class="anomaly-option"
                :class="{ active: formData.anomalySelected.includes(opt.key) }">
                <input
                  type="checkbox"
                  :value="opt.key"
                  v-model="formData.anomalySelected"
                  class="anomaly-checkbox">
                <span class="anomaly-label">{{ opt.label }}</span>
              </label>
            </div>
            <div class="anomaly-prompt-wrapper">
              <div class="anomaly-prompt-label">异常识别指令</div>
              <el-input
                type="textarea"
                v-model="formData.anomalyPrompt"
                :rows="3"
                placeholder="选择异常类型后自动生成，也可手动编辑..."
                resize="none"
                class="supplement-textarea">
              </el-input>
            </div>
          </div>

          <!-- 推送渠道 -->
          <div class="config-section">
            <div class="section-title">
              <i class="el-icon-position" style="color: #4A5FF3;"></i>
              <span>推送渠道</span>
              <span class="section-optional">（不选则不推送）</span>
            </div>
            <push-channel-select
              v-model="pushChannels"
              :agent-id="effectiveAgentId"
              source="create-task-market"
              placeholder="请选择推送渠道"
            />
          </div>

          <!-- 底部操作区 -->
          <div class="config-footer">
            <el-button
              type="primary"
              class="submit-btn"
              :loading="submitting"
              @click="handleSubmitCreate">
              <i class="el-icon-upload2"></i>
              <span>发送创建任务</span>
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 3: 创建成功 -->
    <div v-if="viewState === 'create-success'" class="create-success-view">
      <div class="success-card">
        <div class="success-icon">
          <i class="el-icon-circle-check"></i>
        </div>
        <div class="success-title">任务创建成功</div>
        <div class="success-desc">
          定时任务已创建，可在自动化中查看，按新频次定时生成{{ pageTag }}报告
        </div>

        <div class="success-info-grid">
          <div class="success-info-item success-info-item--full">
            <span class="info-label">任务标题</span>
            <span class="info-value">{{ createdTask.title || (selectedTemplate ? selectedTemplate.templateName : '') }}</span>
          </div>
          <div class="success-info-item">
            <span class="info-label">报告周期</span>
            <span class="info-value">{{ getPeriodLabel(cronPeriod) }}</span>
          </div>
          <div class="success-info-item">
            <span class="info-label">执行时间</span>
            <span class="info-value">{{ getTimeDisplay() }}</span>
          </div>
          <div class="success-info-item success-info-item--full">
            <span class="info-label">补充信息</span>
            <span class="info-value">{{ formData.supplement || '无' }}</span>
          </div>
        </div>

        <div class="success-actions">
          <el-button
            type="primary"
            class="success-btn primary"
            @click="handleGoToAutomation">
            <i class="el-icon-s-operation"></i>
            <span>查看自动化任务</span>
          </el-button>
          <el-button
            class="success-btn secondary"
            @click="handleContinueCreate">
            <i class="el-icon-plus"></i>
            <span>继续创建</span>
          </el-button>
        </div>
      </div>
    </div>

    <!-- 模板预览弹窗 -->
    <el-dialog
      title="模板目标预览"
      :visible.sync="previewDialogVisible"
      width="1000px"
      top="5vh"
      height="90vh"
      append-to-body
      :close-on-click-modal="true"
      custom-class="template-preview-dialog"
      :modal-append-to-body="true">
      <div v-if="previewTemplate" class="dialog-preview-content">
        <!-- 模板目录结构 -->
        <div class="preview-dir-section">
          <div class="pd-title-row">
            <div class="pd-main-title">
              <i class="el-icon-folder-opened"></i>
              <span>模板目录结构</span>
            </div>
            <span style="color: #4a5ff3; font-size: 12px;">{{ getMockName(previewTemplate).cn }}</span>
            <!-- <div class="pd-tag">{{ getMockTag(previewTemplate) }}</div> -->
          </div>
          <!-- <div class="pd-template-name">
            <i :class="getTemplateIcon(previewTemplate)"></i>
            <span>{{ getMockName(previewTemplate).cn }}</span>
            <span class="pd-template-en">{{ getMockName(previewTemplate).en }}</span>
          </div> -->
        </div>

        <!-- 报告结构 -->
        <div class="preview-structure">
          <!-- 核心结论 / 摘要 -->
          <div class="ps-section">
            <div class="ps-section-title">
              <div class="ps-title-left">
                <i class="el-icon-circle-check"></i>
                <span>核心结论 / 摘要</span>
              </div>
              <span class="ps-tag auto">自动生成</span>
            </div>
            <div class="ps-text">3 分钟掌握报告核心结论、关键指标和优先建议。</div>
          </div>

          <!-- 报告正文 -->
          <div class="ps-section ps-section-body">
            <div class="ps-section-title">
              <div class="ps-title-left">
                <i class="el-icon-document"></i>
                <span>报告正文</span>
              </div>
              <span class="ps-tag count">{{ getMockSubsections(previewTemplate).length }} 个章节</span>
            </div>
            <ul class="ps-list">
              <li v-for="(sub, idx) in getMockSubsections(previewTemplate)" :key="idx">
                <div class="ps-list-item-title">
                  <span class="ps-list-dot"></span>
                  <span class="ps-list-title">{{ sub.title }}</span>
                </div>
                <div class="ps-list-desc">{{ sub.desc }}</div>
              </li>
            </ul>
          </div>

          <!-- 数据来源与依据 -->
          <div class="ps-section">
            <div class="ps-section-title">
              <div class="ps-title-left">
                <i class="el-icon-data-analysis"></i>
                <span>数据来源与依据</span>
              </div>
              <span class="ps-tag count">集中展示</span>
            </div>
            <div class="ps-text">
              统一展示数据来源、依据说明和可追溯链接。
            </div>
          </div>
        </div>

        <!-- 底部提示和操作 -->
        <div class="preview-footer-action">
          <div class="pfa-tip">
            <i class="el-icon-info"></i>
            <span>选择该模板后将进入定时周期设置，报告按目标行业、市场范围、关注周期等配置生成。</span>
          </div>
          <el-button
            type="primary"
            class="pfa-btn"
            @click="handleSelectTemplateFromPreview">
            <i class="el-icon-check"></i>
            <span>使用该模板</span>
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { createJobApi, runJobApi } from '@/api/agentAuth'
import { getTemplateList, getTemplateDetail, createReportTitle } from '@/api/agents/agent'
import PushChannelSelect from '@/components/PushChannelSelect.vue'

// 异常告警监控默认选项（模板未配置 alarm_config 时兜底）
const DEFAULT_ANOMALY_OPTIONS = [
  { key: 'revenue_order', label: '收入与订单异常', value: '监测收入达成率低于95%、订单量同比下滑超过10%，出现异常时输出告警' },
  { key: 'profit_cost', label: '毛利与成本异常', value: '监测毛利率低于目标值3pct以上、关键物料成本环比上涨超过5%，出现异常时输出告警' },
  { key: 'delivery_supply', label: '交付与供应异常', value: '监测交付达成率低于90%、设备停机与供应风险，出现交付或供应异常时输出告警' },
  { key: 'payment_cash', label: '回款与现金流异常', value: '监测回款逾期超过30天、经营现金流为负，出现异常时输出告警' }
]

export default {
  name: 'CreateTaskFlowMarket',
  components: { PushChannelSelect },
  props: {
    pageTag: {
      type: String,
      default: ''
    },
    pageCategory: {
      type: String,
      default: 'internal'
    },
    currentAgentId: {
      type: String,
      default: ''
    }
  },
  computed: {
    currentStep() {
      switch (this.viewState) {
        case 'select-template': return 1
        case 'config-template': return 2
        case 'create-success': return 3
        default: return 0
      }
    },
    /** 当前正在配置任务的数字人 ID：props 优先，没有则取 store */
    effectiveAgentId() {
      return this.currentAgentId || (this.$store && this.$store.state && this.$store.state.cosmoClaw && this.$store.state.cosmoClaw.currentAgentId) || ''
    }
  },
  data() {
    return {
      // 视图状态
      viewState: 'select-template', // 'select-template' | 'config-template' | 'create-success'
      // 模板相关
      templateList: [],
      templateLoading: false,
      selectedTemplate: null,
      templateFields: [],
      previewDialogVisible: false,
      previewTemplate: null,
      // 任务创建相关
      submitting: false,
      periodOptions: [
        { label: '日', value: 'daily' },
        { label: '周', value: 'weekly' },
        { label: '月', value: 'monthly' },
        { label: '季度', value: 'quarterly' },
        { label: '半年', value: 'halfYear' },
        { label: '年', value: 'yearly' }
      ],
      cronPeriod: 'daily',
      cronHour: 9,
      cronMinute: 0,
      cronMonthDay: 1,
      cronMonth: 1,
      cronQuarterStart: 1,
      /** 周报周几选择（1-7，1=周一 ... 7=周日） */
      cronWeekDay: 1,
      /** 已选推送渠道 code 列表 */
      pushChannels: [],
      /** 异常告警监控选项：默认 mock，选中模板后若模板配置了 alarm_config 则动态替换 */
      anomalyOptions: DEFAULT_ANOMALY_OPTIONS.map(opt => ({ ...opt })),
      quarterOptions: [
        { value: 1, label: 'Q1(1月)' },
        { value: 4, label: 'Q2(4月)' },
        { value: 7, label: 'Q3(7月)' },
        { value: 10, label: 'Q4(10月)' }
      ],
      cronMinuteOptions: Array.from({ length: 12 }, (_, i) => i * 5),
      formData: {
        templateId: '',
        fields: {},
        supplement: '',
        runImmediately: false,
        anomalySelected: [],
        anomalyPrompt: ''
      },
      createdTask: {
        title: '',
        id: null
      }
    }
  },
  created() {
    this.loadTemplateList()
  },
  watch: {
    // 切换数字人（effectiveAgentId 变化）时，清空已选渠道
    effectiveAgentId: {
      handler(newVal, oldVal) {
        if (!newVal || newVal === oldVal) return
        this.pushChannels = []
      }
    },
    // 异常选项列表变化（切模板、模板配置了 alarm_config）时，
    // 重新校验已选并重新拼接指令
    anomalyOptions(newOptions) {
      const curSelected = this.formData.anomalySelected || []
      const validKeys = (newOptions || []).map(o => o.key)
      const filtered = curSelected.filter(k => validKeys.includes(k))
      if (filtered.length !== curSelected.length) {
        this.formData.anomalySelected = filtered
      }
      const newPrompt = (newOptions || [])
        .filter(o => this.formData.anomalySelected.includes(o.key))
        .map(o => o.value)
        .join('；')
      if (newPrompt) this.formData.anomalyPrompt = newPrompt
    },
    'formData.anomalySelected': {
      handler() {
        const validKeys = this.anomalyOptions.map(o => o.key)
        const cleaned = (this.formData.anomalySelected || []).filter(k => validKeys.includes(k))
        if (cleaned.length !== (this.formData.anomalySelected || []).length) {
          this.formData.anomalySelected = cleaned
        }
        const newPrompt = this.anomalyOptions
          .filter(o => this.formData.anomalySelected.includes(o.key))
          .map(o => o.value)
          .join('；')
        this.formData.anomalyPrompt = newPrompt
      },
      deep: true
    }
  },
  methods: {
    // ===== 模板加载 =====
    async loadTemplateList() {
      this.templateLoading = true
      try {
        const res = await getTemplateList({ templateCategory: '市场洞察' })
        if (res && res.code === 200) {
          const list = Array.isArray(res.data) ? res.data : []
          this.templateList = list
            .filter(t => t.templateStatus === 1)
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        } else {
          console.warn('加载模板列表失败:', res?.message)
        }
      } catch (e) {
        console.error('加载模板列表失败:', e)
      } finally {
        this.templateLoading = false
      }
    },

    // ===== 视图切换 =====
    // 头部加号：重置并回到第一步
    handleOpenCreateTask() {
      this.resetCreateFlow()
      this.viewState = 'select-template'
    },
    // 返回历史报告（通知父组件切回初始视图）
    handleBackToInitial() {
      this.$emit('back')
    },
    resetCreateFlow() {
      this.selectedTemplate = null
      this.templateFields = []
      this.anomalyOptions = DEFAULT_ANOMALY_OPTIONS.map(opt => ({ ...opt }))
      this.formData = {
        templateId: '',
        fields: {},
        supplement: '',
        runImmediately: false,
        anomalySelected: [],
        anomalyPrompt: ''
      }
      this.cronPeriod = 'daily'
      this.cronHour = 9
      this.cronMinute = 0
      this.cronMonthDay = 1
      this.cronMonth = 1
      this.cronQuarterStart = 1
      this.cronWeekDay = 1
      this.pushChannels = []
      this.createdTask = { title: '', id: null }
    },
    handleContinueCreate() {
      this.resetCreateFlow()
      this.viewState = 'select-template'
    },

    // ===== 模板选择 =====
    handlePreviewTemplate(template) {
      this.previewTemplate = template
      this.previewDialogVisible = true
    },
    handleSelectTemplate(template) {
      this.selectedTemplate = template
      this.formData.templateId = template.id
      this.loadTemplateDetail(template.id)
      this.viewState = 'config-template'
    },
    handleSelectTemplateFromPreview() {
      if (this.previewTemplate) {
        this.handleSelectTemplate(this.previewTemplate)
      }
      this.previewDialogVisible = false
    },
    async loadTemplateDetail(templateId) {
      try {
        const res = await getTemplateDetail(templateId)
        if (res && res.code === 200 && res.data) {
          const variables = Array.isArray(res.data.variables) ? res.data.variables : []
          this.templateFields = variables
            .filter(v => v.variableType === 'input')
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
          const newFields = {}
          this.templateFields.forEach(f => { newFields[f.variableKey] = '' })
          this.formData.fields = newFields
          this.applyAnomalyOptions(res.data.alarmConfig)
        } else {
          this.templateFields = []
          this.formData.fields = {}
        }
      } catch (e) {
        console.error('加载模板字段失败:', e)
        this.templateFields = []
      }
    },
    // 应用模板异常告警配置
    applyAnomalyOptions(alarmVar) {
      if (!alarmVar) {
        this.anomalyOptions = DEFAULT_ANOMALY_OPTIONS.map(opt => ({ ...opt }))
        this.formData.anomalySelected = []
        this.formData.anomalyPrompt = ''
        return
      }
      let options = []
      try {
        const parsed = typeof alarmVar === 'string' ? JSON.parse(alarmVar) : alarmVar
        if (Array.isArray(parsed)) {
          options = parsed.map(item => ({
            key: item.key || item.name || '',
            label: item.label || item.name || '',
            value: item.value || item.prompt || item.desc || ''
          })).filter(o => o.key && o.label)
        }
      } catch (e) {
        console.warn('解析模板 alarm_config 失败:', e)
      }
      this.anomalyOptions = options.length > 0
        ? options
        : DEFAULT_ANOMALY_OPTIONS.map(opt => ({ ...opt }))
      this.formData.anomalySelected = []
      this.formData.anomalyPrompt = ''
    },

    // ===== 辅助方法 =====
    getTemplateIcon(template) {
      const code = (template.templateCode || '').toLowerCase()
      const name = (template.templateName || '').toLowerCase()
      const iconMap = {
        'trend': 'el-icon-data-line',
        'market': 'el-icon-s-marketing',
        'industry': 'el-icon-s-platform',
        'category': 'el-icon-goods',
        'product': 'el-icon-goods',
        'chain': 'el-icon-share',
        'customer': 'el-icon-user',
        'client': 'el-icon-user',
        'invest': 'el-icon-s-finance',
        'compet': 'el-icon-s-flag',
        'risk': 'el-icon-warning',
        'finance': 'el-icon-s-finance',
        'report': 'el-icon-document'
      }
      for (const [key, icon] of Object.entries(iconMap)) {
        if (code.includes(key)) return icon
      }
      const nameIconMap = {
        '趋势': 'el-icon-data-line',
        '行业': 'el-icon-s-platform',
        '品类': 'el-icon-goods',
        '产品': 'el-icon-goods',
        '产业链': 'el-icon-share',
        '客户': 'el-icon-user',
        '投资': 'el-icon-s-finance',
        '竞争': 'el-icon-s-flag',
        '风险': 'el-icon-warning',
        '市场': 'el-icon-s-marketing',
        '财务': 'el-icon-s-finance'
      }
      for (const [key, icon] of Object.entries(nameIconMap)) {
        if (name.includes(key)) return icon
      }
      return 'el-icon-document'
    },
    getTemplateColor(template) {
      const key = template.templateCode || template.templateName || ''
      const colors = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
        'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
      ]
      let hash = 0
      for (let i = 0; i < key.length; i++) {
        hash = key.charCodeAt(i) + ((hash << 5) - hash)
      }
      return colors[Math.abs(hash) % colors.length]
    },
    // ===== 模板Mock数据 =====
    getMockName(template) {
      const key = (template.templateCode || template.templateName || '').toLowerCase()
      const mockMap = {
        'market_trend_report': { cn: '市场趋势', en: 'Market Trend' },
        'industry_panorama_report': { cn: '行业全景', en: 'Industry Overview' },
        'competitive_analysis_report': { cn: '产品分析', en: 'Product Analysis' },
        'industry_value_chain_report': { cn: '产业链分析报告', en: 'Industry Chain Analysis' },
        'customer_insight_report': { cn: '客户洞察', en: 'Customer Insight' },
        'investment_opportunity_report': { cn: '投资机会', en: 'Investment Opportunity' },
        'technology_product_trend_report': { cn: '技术与产品趋势', en: 'Tech & Product Trend' },
        'enterprise_benchmark_report': { cn: '商业模式研究', en: 'Business Model Research' },
        'comprehensive_insight_report': { cn: '竞争格局研究', en: 'Competitive Landscape' }
      }
      for (const [code, val] of Object.entries(mockMap)) {
        if (key.includes(code)) return val
      }
      return {
        cn: template.templateName || '未知模板',
        en: 'Report Template'
      }
    },
    getMockDesc(template) {
      const key = (template.templateCode || template.templateName || '').toLowerCase()
      const descMap = {
        'market_trend_report': '本报告聚焦目标行业市场规模、增长规模、发展环境、技术演进与竞争格局，输出近3-5年数据洞察与趋势预测。',
        'industry_panorama_report': '聚焦目标行业全景概况，涵盖行业定义、分类体系与TAM/SAM/SOM市场规模，梳理上下游格局、关键参与者与发展趋势。',
        'competitive_analysis_report': '聚焦产品矩阵竞争态势，从市场定位、功能设计、价格、渠道、客户反馈等维度进行对比分析，输出可执行建议。',
        'industry_value_chain_report': '产业链全景图谱，分析上下游供需、价值链分布、关键环节利润与产业布局，研判产业转移与整合趋势。',
        'customer_insight_report': '客户画像与行为数据洞察，覆盖客户分层、需求偏好、购买决策与满意度分析，助力精准营销与客户运营。',
        'investment_opportunity_report': '筛选高潜力赛道与标的，聚焦市场增速、竞争格局、盈利模式与退出路径，提供风险收益评估与投资建议。',
        'technology_product_trend_report': '聚焦前沿技术演进与产品创新趋势，评估技术成熟度与应用前景，为企业技术路线规划提供决策支持。',
        'enterprise_benchmark_report': '聚焦商业模式创新与标杆案例研究，分析盈利模式、成本结构与护城河，为企业战略转型提供参考。',
        'comprehensive_insight_report': '全景扫描竞争格局，对标标杆企业研究其战略布局、产品矩阵与核心能力，识别差异化优势与机会。'
      }
      for (const [code, desc] of Object.entries(descMap)) {
        if (key.includes(code)) return desc
      }
      return template.templateDesc || '本报告提供专业的行业分析与洞察，助力决策。'
    },
    getMockSubsections(template) {
      const key = (template.templateCode || template.templateName || '').toLowerCase()
      const subMap = {
        'market_trend_report': [
          { title: '行业定义与边界', desc: '明确行业定义、产业边界、统计口径和相关概念区分。' },
          { title: '宏观环境 PESTEL', desc: '从政策、经济、社会、技术、环境、法律六个维度分析外部环境。' },
          { title: '中国或目标市场现状', desc: '输出市场规模、产业结构、渗透率、区域格局和企业主体。' },
          { title: '驱动与制约因素', desc: '归纳政策、技术、需求、成本等驱动因素，以及人才、数据、安全、预算等制约因素。' },
          { title: '五大维度趋势判断', desc: '从技术、产品、竞争、供需、区域五个维度给出趋势判断和依据。' },
          { title: '未来 5 年市场规模与增速预测', desc: '给出 2026-2030 年规模预测、增速、关键假设和主要风险。' },
          { title: '新兴应用与增量市场', desc: '覆盖 5G 工厂、工业大模型、数据要素、绿色低碳、中小企业转型等增量方向。' },
          { title: '机会与建议', desc: '按优先级输出机会窗口、建议动作、预期结果和主要风险。' }
        ],
        'industry_panorama_report': [
          { title: '行业定义与分类体系', desc: '明确行业定义、产业边界、统计口径和相关概念区分。' },
          { title: '全球及中国市场规模现状', desc: '输出市场规模、产业结构、渗透率、区域格局和企业主体。' },
          { title: 'TAM/SAM/SOM 市场规模测算', desc: '从总可寻址、可服务、可获得三个层次估算市场空间。' },
          { title: '供需格局', desc: '分析供需两侧结构、产能、库存、渠道库存与价格传导机制。' },
          { title: '进出口贸易', desc: '分析工业软件、设备、服务等进出口规模、结构、来源地和目的地。' },
          { title: '经营效益与价格行情', desc: '分析企业营收、利润、毛利率及市场价格变化。' },
          { title: '行业痛点', desc: '梳理数据标准、人才、交付、成本、安全等核心行业痛点。' }
        ],
        'competitive_analysis_report': [
          { title: '波特五力', desc: '从竞争者、供应商、客户、替代品、潜在进入者五维度分析竞争强度。' },
          { title: '市场集中度与竞争梯队', desc: '识别头部、腰部、尾部玩家，分析CR3/CR5集中度与梯队结构。' },
          { title: '市场份额与排名', desc: '输出Top企业份额、排名、增长趋势与核心业务占比。' },
          { title: '不同派系、价格带、产品级别竞争', desc: '按技术路线、品牌定位、价格段、产品级别进行竞争分组分析。' },
          { title: '区域竞争格局', desc: '识别重点产业集群、区域活跃度和主要平台区域布局。' },
          { title: '重点竞品深度对标', desc: '对主要竞品的企业背景、经营、产品、渠道、客户、组织进行深度对标。' },
          { title: '产品、价格、渠道、客户、组织差异', desc: '从五个维度输出竞品差异化能力矩阵和攻防切入点。' },
          { title: '攻防策略建议', desc: '输出进攻策略、防守策略、优先级和资源投入建议。' }
        ],
        'industry_value_chain_report': [
          { title: '产业链结构图谱', desc: '绘制上游原材料、中游零部件/整机、下游应用领域的完整产业图谱。' },
          { title: '价值链成本结构与价值分布', desc: '拆解各环节成本构成与价值分配，识别利润池与瓶颈环节。' },
          { title: '价格传导机制', desc: '分析上游价格波动如何传导至中下游，以及各环节的议价能力与缓冲机制。' },
          { title: '上游供应市场', desc: '分析核心原材料供应格局、价格趋势、产能布局与供应链风险。' },
          { title: '中游制造与竞争格局', desc: '分析平台、工业软件、设备和集成商等中游竞争格局。' },
          { title: '下游应用领域与渠道', desc: '分析制造、能源、汽车、电子等下游应用和渠道模式。' },
          { title: '供应链瓶颈与风险', desc: '识别关键设备、核心软件、数据安全等供应链瓶颈。' },
          { title: '产业链薄弱环节与机会', desc: '识别高价值、可切入、国产替代和生态合作机会。' }
        ],
        'customer_insight_report': [
          { title: '客户或用户画像', desc: '基于年龄、性别、地域、收入、职业等维度构建客户画像。' },
          { title: '需求与痛点', desc: '识别客户核心需求、未被满足的痛点及优先级排序。' },
          { title: '消费或使用行为', desc: '分析购买决策路径、使用频率、场景与行为特征。' },
          { title: '决策路径与信息渠道', desc: '梳理客户从认知到购买的完整决策路径与关键信息触点。' },
          { title: '付费意愿与价格敏感度', desc: '评估不同客户层级的预算、付费意愿和价格敏感度。' },
          { title: 'B2B 采购决策链或 B2C 消费者行为', desc: '输出 B2B 采购角色、决策链或 B2C 消费者行为路径。' },
          { title: '细分人群分层经营策略', desc: '按客户规模、行业、场景分层给出经营策略。' }
        ],
        'investment_opportunity_report': [
          { title: '行业前景与市场规模预测', desc: '基于历史数据与趋势预测行业增长率、规模与天花板。' },
          { title: '投融资与资本市场动向', desc: '梳理近年投融资事件、金额、赛道分布与退出案例。' },
          { title: 'SWOT 分析', desc: '从优势、劣势、机会、威胁四维度进行战略分析。' },
          { title: '进入与退出壁垒', desc: '分析技术壁垒、资金壁垒、牌照壁垒与退出路径设计。' },
          { title: '投资价值评估', desc: '评估行业吸引力、企业成长性和投资回报潜力。' },
          { title: '重点投资领域、地区与空白点', desc: '识别值得投资的细分领域、区域和市场空白。' },
          { title: '风险预警', desc: '输出市场、政策、技术、竞争和数据安全风险。' },
          { title: '投资策略建议', desc: '给出投资、合作、并购和退出策略建议。' }
        ],
        'technology_product_trend_report': [
          { title: '核心技术路线', desc: '梳理行业主流技术路线、创新方向与技术迭代路径。' },
          { title: '专利与研发活跃度', desc: '分析核心玩家专利布局、研发投入强度与技术热点。' },
          { title: '产品迭代方向', desc: '识别产品演进方向、新一代产品形态与颠覆性创新机会。' },
          { title: '智能化、国产化、绿色化趋势', desc: '分析三大趋势对行业技术路线与产品布局的影响。' },
          { title: '技术成熟度判断', desc: '评估关键技术成熟度、落地周期和商业化进度。' },
          { title: '未来产品路线图', desc: '按短中长期输出产品演进路线和版本规划。' },
          { title: '技术壁垒与竞争优势来源', desc: '识别数据、算法、场景、生态和工程化壁垒。' }
        ],
        'enterprise_benchmark_report': [
          { title: '国内外代表性企业 3-10 家', desc: '选取国内外标杆企业作为研究对象，涵盖不同规模与模式。' },
          { title: '基本信息', desc: '梳理企业成立时间、总部、员工数、创始人、股权结构等基本信息。' },
          { title: '经营状况', desc: '分析营收、利润、增长趋势、毛利率、费用率等核心经营指标。' },
          { title: '业务与产品布局', desc: '梳理企业业务版图、产品线、区域布局与全球化策略。' },
          { title: '销售渠道与客户', desc: '分析渠道结构、客户行业、大客户和区域覆盖。' },
          { title: '发展战略与最新动向', desc: '跟踪并购、生态、产品发布和区域扩张动向。' },
          { title: '优劣势对标', desc: '输出企业能力优势、短板和可借鉴经验。' },
          { title: '总结标杆企业成功经验和可借鉴能力', desc: '提炼可复制的组织、产品、渠道和生态打法。' }
        ],
        'comprehensive_insight_report': [
          { title: '执行摘要', desc: '概括报告核心结论、关键发现与战略建议。' },
          { title: '行业综述', desc: '梳理行业发展历程、现状、主要玩家与竞争格局演变。' },
          { title: 'PESTEL 宏观环境', desc: '从六大维度分析行业所处的宏观环境与影响因素。' },
          { title: '中国或目标市场现状', desc: '聚焦目标市场规模、结构、渗透率与企业格局。' },
          { title: 'TAM/SAM/SOM 市场规模', desc: '从总体、可服务、可获得市场三层测算规模。' },
          { title: '市场趋势与前景预测', desc: '输出增长驱动、五大趋势和未来 5 年预测。' },
          { title: '竞争格局与竞品分析', desc: '分析竞争梯队、份额、五力和重点企业。' },
          { title: '产业链与价值链', desc: '拆解上中下游、价值分布和价格传导。' },
          { title: '细分市场分析', desc: '对不同产品、行业、区域细分市场逐一评估。' },
          { title: '客户洞察', desc: '建立客户画像、需求、决策链和分层策略。' },
          { title: '重点企业研究', desc: '深度研究代表企业的经营、产品和战略。' },
          { title: '投融资与资本市场', desc: '分析融资、并购、上市和资本流向。' },
          { title: '技术与产品趋势', desc: '梳理技术路线、研发活跃度和产品演进。' },
          { title: 'SWOT 与投资价值', desc: '评估优势劣势、机会威胁和投资价值。' },
          { title: '风险预警与应对', desc: '输出风险清单、影响评估和应对预案。' },
          { title: '战略建议', desc: '输出市场进入、竞争应对和资源配置建议。' }
        ]
      }
      for (const [code, subs] of Object.entries(subMap)) {
        if (key.includes(code)) return subs
      }
      return [
        { title: '核心指标分析', desc: '分析核心业务指标与驱动因素。' },
        { title: '趋势判断', desc: '识别行业发展趋势与转折点。' },
        { title: '关键发现', desc: '提炼报告核心发现与洞察。' },
        { title: '建议措施', desc: '提供可执行的行动建议。' }
      ]
    },
    getMockTag(template) {
      const key = (template.templateCode || template.templateName || '').toLowerCase()
      if (key.includes('market') || key.includes('industry') || key.includes('product')) {
        return '市场研究'
      }
      if (key.includes('customer')) {
        return '用户洞察'
      }
      if (key.includes('invest')) {
        return '投资分析'
      }
      if (key.includes('chain')) {
        return '产业研究'
      }
      if (key.includes('tech')) {
        return '技术趋势'
      }
      if (key.includes('business')) {
        return '商业分析'
      }
      if (key.includes('compet')) {
        return '竞争研究'
      }
      return '市场洞察'
    },
    getPeriodLabel(value) {
      const map = {
        'daily': '每日',
        'weekly': '每周',
        'monthly': '每月',
        'quarterly': '每季度',
        'halfYear': '每半年',
        'yearly': '每年'
      }
      return map[value] || value
    },
    getTimeDisplay() {
      const m = this.padZero(this.cronMinute)
      const h = this.padZero(this.cronHour)
      switch (this.cronPeriod) {
        case 'daily':
          return `每天 ${h}:${m}`
        case 'weekly':
          return `每周${this.weekDayLabel(this.cronWeekDay)} ${h}:${m}`
        case 'monthly':
          return `每月 ${this.cronMonthDay}日 ${h}:${m}`
        case 'quarterly':
          return `每季度Q${this.cronQuarterStart}月 ${this.cronMonthDay}日 ${h}:${m}`
        case 'halfYear':
          return `每半年 ${this.cronMonth}月${this.cronMonthDay}日 ${h}:${m}`
        case 'yearly':
          return `每年 ${this.cronMonth}月${this.cronMonthDay}日 ${h}:${m}`
        default:
          return `${h}:${m}`
      }
    },
    padZero(n) {
      return n < 10 ? '0' + n : '' + n
    },
    /**
     * 周几下拉展示文案：周一..周日
     */
    weekDayLabel(d) {
      const names = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
      const idx = Math.max(0, Math.min(6, (d || 1) - 1))
      return names[idx]
    },
    // ===== 提交创建任务 =====
    async handleSubmitCreate() {
      if (!this.selectedTemplate) {
        this.$message.warning('请选择模板')
        return
      }
      this.submitting = true
      try {
        // Step 1: 生成任务标题和内容
        const genRes = await createReportTitle({
          id: this.selectedTemplate.id,
          variableValues: this.formData.fields,
          supplement: this.formData.supplement
        })
        if (!genRes || genRes.code !== 200 || !genRes.data) {
          this.$message.error(genRes?.message || '生成任务内容失败')
          return
        }
        const { title, content } = genRes.data
        if (!title || !content) {
          this.$message.error('生成的任务标题或内容为空')
          return
        }

        // Step 2: 创建任务
        const finalAgentId = this.effectiveAgentId
        const cronExp = this.computeCronExpression()
        const reportConfig = {
          reportType: this.pageCategory === 'external' ? 'market' : 'internal',
          category: this.pageCategory,
          decision: false
        }
        // 推送渠道：去重 + 去空，约定嵌入 reportConfig.pushChannels
        const channels = Array.from(new Set((this.pushChannels || []).filter(Boolean)))
        if (channels.length) {
          reportConfig.pushChannels = channels
        }
        // 异常告警监控
        reportConfig.anomalyMonitor = {
          enabled: !!(this.formData.anomalyPrompt && this.formData.anomalyPrompt.trim()),
          prompt: this.formData.anomalyPrompt || ''
        }
        const createParams = {
          name: title,
          agentId: finalAgentId,
          taskType: 'report',
          cronExpression: cronExp,
          enabled: true,
          triggerMessage: content,
          source: 'manual',
          reportConfig: JSON.stringify(reportConfig)
        }
        const createRes = await createJobApi(createParams)
        const isSuccess = !createRes || createRes.code === 200 || createRes.code === '200' || createRes.code === 0
        if (!isSuccess) {
          this.$message.error(createRes?.message || '创建任务失败')
          return
        }

        const newTaskId = createRes?.data?.id || createRes?.id || null

        // Step 3: 如果需要立即执行
        if (this.formData.runImmediately && newTaskId) {
          try {
            const runRes = await runJobApi(newTaskId)
            const runOk = !runRes || runRes.code === 200 || runRes.code === '200' || runRes.code === 0
            if (runOk) {
              this.$message.success('已触发立即执行')
            }
          } catch (re) {
            console.error('立即执行失败:', re)
          }
        }

        this.createdTask = { title, id: newTaskId }
        this.viewState = 'create-success'
        // 通知父组件任务已创建（父组件可刷新报告列表）
        this.$emit('created', { title, id: newTaskId })
      } catch (e) {
        console.error('提交任务失败:', e)
        this.$message.error(e?.message || '提交任务失败')
      } finally {
        this.submitting = false
      }
    },
    computeCronExpression() {
      const m = this.cronMinute
      const h = this.cronHour
      const d = this.cronMonthDay
      switch (this.cronPeriod) {
        case 'daily':
          return `${m} ${h} * * ?`
        case 'weekly':
          return `${m} ${h} ? * ${this.cronWeekDay}`
        case 'monthly':
          return `${m} ${h} ${d} * ?`
        case 'quarterly':
          return `${m} ${h} ${d} ${this.cronQuarterStart},${this.cronQuarterStart + 3},${this.cronQuarterStart + 6},${this.cronQuarterStart + 9} ?`
        case 'halfYear':
          return `${m} ${h} ${d} 1,7 ?`
        case 'yearly':
          return `${m} ${h} ${d} ${this.cronMonth} ?`
        default:
          return `${m} ${h} * * ?`
      }
    },

    // ===== 导航 =====
    // 跳转自动化任务页（交由父组件处理路由）
    handleGoToAutomation() {
      this.$emit('go-automation')
    }
  }
}
</script>

<style lang="scss" scoped>
.create-task-flow {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.main-title {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  font-family: PingFangSC-Bold;
  flex-shrink: 0;
  display: inline-block;
}
/* 加号按钮 */
.chat-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 2px;
  cursor: pointer;
  padding: 2px 8px;
  color: #4a5ff3 !important;
  transition: all 0.2s;
  border: 1px solid #4a5ff3;
  &:hover {
    background: #f0f2ff;
  }
}
.chat-tag i {
  font-size: 15px;
  font-weight: 400;
}
/* 历史报告返回标签 */
.back-label {
  font-size: 12px;
  color: #4a5ff3;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

/* ===== 创建任务流程样式 ===== */
.create-task-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  flex-shrink: 0;
}

/* 步骤指示器 */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 40px;
  background: #f7f8fc;
  border-radius: 12px;
  gap: 0;
}
.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  opacity: 0.5;
  &.active {
    opacity: 1;
    .step-dot {
      background: #4A5FF3;
      color: #fff;
    }
    .step-label {
      color: #4A5FF3;
      font-weight: 600;
    }
  }
  &.completed {
    opacity: 1;
    .step-dot {
      background: #4A5FF3;
      color: #fff;
    }
  }
}
.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e8ecf0;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s;
}
.step-label {
  font-size: 13px;
  color: #666;
  transition: all 0.3s;
}
.step-line {
  flex: 1;
  height: 2px;
  background: #e8ecf0;
  margin: 0 8px;
  margin-bottom: 32px;
  border-radius: 1px;
  transition: all 0.3s;
  // &.active {
  //   background: #4A5FF3;
  // }
}

/* Step 1: 模板选择 */
.template-select-view {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
  min-height: 0;
}
.template-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.header-actions {
  display: flex;
  align-items: center;
}
.back-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #4A5FF3;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 4px;
  transition: all 0.2s;
  i {
    font-size: 14px;
  }
  &:hover {
    background: #f0f2ff;
  }
}
.template-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
  i {
    font-size: 48px;
    margin-bottom: 12px;
    color: #ccc;
  }
  p {
    font-size: 14px;
    margin: 0;
  }
}
.view-intro {
  margin-bottom: 20px;
}
.view-intro-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}
.view-intro-desc {
  font-size: 13px;
  color: #999;
}
.template-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.template-card {
  background: #fff;
  border: 1px solid #ebedf0;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  min-height: 240px;
  &:hover {
    border-color: #4A5FF3;
    box-shadow: 0 4px 16px rgba(74, 95, 243, 0.12);
    // .tc-select-btn {
    //   background: #4A5FF3;
    //   color: #fff;
    // }
  }
}
.tc-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.template-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  i {
    font-size: 22px;
    color: #fff;
  }
}
.template-meta-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.template-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
}
.template-name-en {
  font-size: 11px;
  color: #999;
  line-height: 1.2;
  margin-top: 2px;
}
.template-info {
  margin-bottom: 10px;
}
.template-desc {
  font-size: 12px;
  color: #666;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
.tc-subsections {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
  min-height: 80px;
}
.tc-sub-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.tc-sub-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4A5FF3;
  margin-top: 6px;
  flex-shrink: 0;
}
.tc-sub-text {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
}
.tc-footer {
  display: flex;
  gap: 10px;
  padding-top: 4px;
}
.tc-tag {
  font-size: 11px;
  color: #4A5FF3;
  background: #f0f2ff;
  padding: 2px 8px;
  border-radius: 4px;
}
.tc-select-btn {
  flex: 1;
  height: 36px;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  font-size: 13px;
  color: #666;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
  white-space: nowrap;
  i {
    font-size: 14px;
  }
  &:hover {
    border-color: #4A5FF3;
    color: #4A5FF3;
  }
}
.tc-select-btn-primary {
  background: #4A5FF3;
  border-color: #4A5FF3;
  color: #fff;
  &:hover {
    background: #3d50e0;
    border-color: #3d50e0;
    color: #fff;
  }
}
.tc-select-btn-outline {
  background: #fff;
  border-color: #ebedf0;
  color: #666;
  &:hover {
    border-color: #4A5FF3;
    color: #4A5FF3;
  }
}

/* Step 2: 模板配置 */
.template-config-view {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
  min-height: 0;
}
.config-layout {
  display: flex;
  gap: 20px;
  min-height: 0;
}
.config-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.config-right {
  width: 380px;
  flex-shrink: 0;
}
.selected-template-card {
  background: #fff;
  border: 1px solid #ebedf0;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.stc-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.stc-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  i {
    font-size: 18px;
    color: #fff;
  }
}
.stc-info {
  display: flex;
  flex-direction: column;
}
.stc-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 2px;
}
.stc-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}
.stc-link {
  font-size: 13px;
  color: #4A5FF3;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.config-row {
  display: flex;
  gap: 16px;
  width: 100%;
}
.config-section {
  flex: 1;
  background: #fff;
  border: 1px solid #ebedf0;
  border-radius: 12px;
  padding: 16px;
}
.config-section-period {
  // flex: 0 0 320px;
  // min-width: 320px;
}
.config-section-optional {
  // flex: 1;
  // min-width: 0;
}
.section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 14px;
  i {
    color: #4A5FF3;
    font-size: 16px;
  }
}
.section-optional {
  font-size: 12px;
  color: #999;
  font-weight: 400;
}
.section-count {
  font-size: 12px;
  color: #999;
  font-weight: 400;
  margin-left: auto;
}
.period-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
}
.period-label {
  font-size: 13px;
  color: #666;
  width: 90px;
  flex-shrink: 0;
}
.period-select {
  width: 180px;
}
.time-selects {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.time-label {
  font-size: 13px;
  color: #666;
}
.time-sel {
  width: 100px;
}
.time-sel-day {
  width: 80px;
}
.time-sel-quarter {
  width: 120px;
}
.time-sep {
  font-size: 14px;
  color: #999;
  font-weight: 600;
}
.template-fields-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 16px;
}
.template-field-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tf-label {
  font-size: 12px;
  color: #666;
}
.tf-optional {
  color: #c0c4cc;
}
.tf-input {
  width: 100%;
}
.no-fields-tip {
  font-size: 12px;
  color: #999;
  padding: 8px 0;
}
.supplement-textarea {
  width: 100%;
}

/* 异常告警监控 */
.anomaly-options {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}
.anomaly-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  color: #606266;
  background: #fff;
  &:hover {
    border-color: #E6A23C;
    color: #E6A23C;
  }
  &.active {
    border-color: #E6A23C;
    background: #fdf6ec;
    color: #E6A23C;
  }
}
.anomaly-checkbox {
  accent-color: #E6A23C;
  width: 14px;
  height: 14px;
  margin: 0;
}
.anomaly-label {
  user-select: none;
}
.anomaly-prompt-wrapper {
  margin-top: 8px;
}
.anomaly-prompt-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.config-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}
.submit-btn {
  padding: 10px 32px;
  font-size: 14px;
  background: #4A5FF3;
  border-color: #4A5FF3;
  border-radius: 8px;
  i {
    margin-right: 6px;
  }
  &:hover {
    background: #3d50e0;
    border-color: #3d50e0;
  }
}

/* 预览卡片 */
.preview-card {
  background: #fff;
  border: 1px solid #ebedf0;
  border-radius: 12px;
  overflow: hidden;
  position: sticky;
  top: 0;
}
.preview-header {
  padding: 14px 16px;
  border-bottom: 1px solid #ebedf0;
}
.preview-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  i {
    color: #4A5FF3;
  }
}
.preview-content {
  padding: 16px;
  max-height: 500px;
  overflow-y: auto;
}
.preview-section {
  margin-bottom: 18px;
  &:last-child {
    margin-bottom: 0;
  }
}
.preview-section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  i {
    color: #4A5FF3;
  }
}
.preview-count {
  font-size: 11px;
  color: #999;
  font-weight: 400;
  margin-left: auto;
}
.preview-desc {
  font-size: 12px;
  color: #666;
  line-height: 1.6;
}
.preview-list {
  list-style: none;
  padding: 0;
  margin: 0;
  li {
    font-size: 12px;
    color: #666;
    padding: 4px 0;
    padding-left: 16px;
    position: relative;
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 10px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #4A5FF3;
    }
  }
}
.preview-footer {
  padding: 12px 16px;
  background: #f7f8fc;
  border-top: 1px solid #ebedf0;
  text-align: center;
}
.preview-footer-text {
  font-size: 12px;
  color: #999;
}

/* Step 3: 创建成功 */
.create-success-view {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.success-card {
  width: 580px;
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
}
.success-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  i {
    font-size: 36px;
    color: #fff;
  }
}
.success-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}
.success-desc {
  font-size: 13px;
  color: #999;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 24px;
}
.success-info-grid {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;
  margin-bottom: 28px;
}
.success-info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  &--full {
    grid-column: 1 / -1;
  }
}
.info-label {
  font-size: 12px;
  color: #999;
}
.info-value {
  font-size: 13px;
  color: #333;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.success-actions {
  display: flex;
  gap: 12px;
  width: 100%;
}
.success-btn {
  flex: 1;
  height: 40px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &.primary {
    background: #4A5FF3;
    border-color: #4A5FF3;
    color: #fff;
    &:hover {
      background: #3d50e0;
      border-color: #3d50e0;
    }
  }
  &.secondary {
    border: 1px solid #ebedf0;
    color: #666;
    background: #fff;
    &:hover {
      border-color: #4A5FF3;
      color: #4A5FF3;
    }
  }
}

/* 模板预览弹窗 */
.dialog-preview-content {
    padding: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
}
.preview-dir-section {
  margin-bottom: 16px;
  // padding-bottom: 14px;
  // border-bottom: 1px solid #ebedf0;
}
.pd-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.pd-main-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  i {
    color: #4A5FF3;
  }
}
.pd-tag {
  font-size: 12px;
  color: #4A5FF3;
  background: #f0f2ff;
  padding: 4px 10px;
  border-radius: 12px;
}
.pd-template-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #4A5FF3;
  padding-left: 4px;
  i {
    font-size: 16px;
  }
  .pd-template-en {
    font-size: 12px;
    color: #999;
    font-weight: 400;
    font-style: italic;
  }
}
.preview-structure {
  flex: 1;
  // max-height: 360px;
  overflow-y: auto;
  // padding-right: 8px;
  margin-bottom: 16px;
  background: #f5f6fa;
  // padding: 12px;
  border-radius: 8px;
}
.ps-section {
  background: #fff;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
}
.ps-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.ps-title-left {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  i {
    color: #4A5FF3;
    font-size: 14px;
  }
}
.ps-tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  &.auto {
    color: #4A5FF3;
    background: #f0f2ff;
  }
  &.count {
    color: #999;
    background: #f5f6fa;
  }
}
.ps-text {
  font-size: 12px;
  color: #666;
  line-height: 1.6;
  padding-left: 20px;
}
.ps-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.ps-list li {
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f5;
  &:last-child {
    border-bottom: none;
  }
}
.ps-list-item-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 3px;
}
.ps-list-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #4A5FF3;
  flex-shrink: 0;
}
.ps-list-title {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}
.ps-list-desc {
  font-size: 12px;
  color: #999;
  line-height: 1.5;
  padding-left: 11px;
}
.preview-footer-action {
  border-top: 1px solid #ebedf0;
  padding-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.pfa-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #999;
  line-height: 1.5;
  i {
    color: #4A5FF3;
  }
}
.pfa-btn {
  padding: 8px 20px;
  font-size: 13px;
  border-radius: 6px;
  background: #4A5FF3;
  border-color: #4A5FF3;
  i {
    margin-right: 4px;
  }
  &:hover {
    background: #3d50e0;
    border-color: #3d50e0;
  }
}
</style>

<style lang="scss">
/* 覆盖样式：模板预览弹窗（dialog 为 append-to-body，挂载到 body 下，
   Vue 2 scoped 样式无法穿透到 body，故保留非 scoped 块；
   .template-preview-dialog 为本组件唯一的自定义类名，不构成全局污染） */
.template-preview-dialog {
  height: 90vh !important;
  max-height: 100vh !important;
  .el-dialog__body {
    padding: 16px 20px;
    // max-height: 560px;
    overflow-y: auto;
    background: #f6f5fa;
  }
  .el-dialog__footer {
    display: none;
  }
}
</style>
